import React, { useState, useRef, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Avatar } from '@/components/Avatar';
import { Loading } from '@/components/Loading';
import { RichTextEditor, type RichTextEditorRef } from '@/components/editor/RichTextEditor';
import { RichTextRenderer } from '@/components/editor/RichTextRenderer';
import { ticketService } from '@/services/ticketService';
import { attachmentService } from '@/services/attachmentService';
import { appConstants } from '@/config/app';
import { getRoleDisplayName } from '@/utils/roleUtils';
import { getRelativeTimeSydney, formatWithTimezone } from '@/utils/timezone';
import { ConversationType, EmailDeliveryStatus, EmailDirection, UserRole, TicketStatus } from '@/types/api';
import type { ApiError, Ticket, Conversation, CreateConversationRequest } from '@/types/api';
import {
  getAttachmentDisplayName,
  getAttachmentSize,
  getConversationKind,
  getConversationLabel,
  getEmailDeliveryPresentation,
  getLatestEmailDelivery,
  normaliseConversation,
} from './conversationPresentation';

interface EnhancedConversationPanelProps {
  ticket: Ticket;
  onTicketUpdate: (updates: Partial<Ticket>) => void;
  canReply: boolean;
  canEdit: boolean;
  className?: string;
}

/**
 * Enhanced Conversation Panel Component
 * 
 * Loads the real conversation history for the ticket and lets the user reply:
 * - Rich text editor for public replies and internal notes
 * - File uploads and attachments
 */
export const EnhancedConversationPanel: React.FC<EnhancedConversationPanelProps> = ({
  ticket,
  onTicketUpdate,
  canReply,
  canEdit,
  className
}) => {
  // State
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterType, setFilterType] = useState<'all' | 'public' | 'internal' | 'activity'>('all');
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [replyContent, setReplyContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [uploadedAttachmentIds, setUploadedAttachmentIds] = useState<string[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deliveryNotice, setDeliveryNotice] = useState<{ deliveryId?: string; message: string } | null>(null);
  const [retryingDeliveryIds, setRetryingDeliveryIds] = useState<Set<string>>(new Set());
  const [retryErrors, setRetryErrors] = useState<Record<string, string>>({});
  const [attachmentDownloadErrors, setAttachmentDownloadErrors] = useState<Record<string, string>>({});
  const [showSendActionsDropdown, setShowSendActionsDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');

  // Refs
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const replyEditorRef = useRef<RichTextEditorRef>(null);
  const sendDropdownRef = useRef<HTMLDivElement>(null);

  // Send action options for dropdown
  const sendActions = [
    { 
      label: 'Send', 
      value: null, 
      icon: '📤',
      description: 'Send reply without changing ticket status' 
    },
    { 
      label: 'Send and Close', 
      value: TicketStatus.Closed, 
      icon: '✅',
      description: 'Send reply and mark ticket as closed' 
    },
    { 
      label: 'Send and Resolve', 
      value: TicketStatus.Resolved, 
      icon: '✅',
      description: 'Send reply and mark ticket as resolved' 
    },
    { 
      label: 'Send and Put On Hold', 
      value: TicketStatus.OnHold, 
      icon: '⏸️',
      description: 'Send reply and put ticket on hold' 
    },
    { 
      label: 'Send and Mark as Pending', 
      value: TicketStatus.Pending, 
      icon: '⏳',
      description: 'Send reply and mark as pending customer response' 
    }
  ];

  const loadConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    setConversationError(null);
    try {
      const response = await ticketService.getTicketConversations(ticket.id);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to load the conversation history.');
      }
      setConversations((response.data.conversations || []).map(normaliseConversation));
    } catch (error) {
      setConversations([]);
      setConversationError(error instanceof Error ? error.message : 'Failed to load the conversation history.');
    } finally {
      setIsLoadingConversations(false);
    }
  }, [ticket.id]);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [conversations]);

  // Handle reply content changes
  const handleReplyContentChange = useCallback((content: string) => {
    setReplyContent(content);
    setMutationError(null);
  }, []);

  const resetComposer = useCallback(() => {
    setReplyContent('');
    setIsInternal(false);
    setPendingAttachments([]);
    setUploadedAttachmentIds([]);
    setAttachmentError(null);
    setMutationError(null);
    setShowSendActionsDropdown(false);
    setShowReplyForm(false);
    replyEditorRef.current?.setContent('');
  }, []);

  const handleAttachmentSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selectedFiles.length) return;

    const combinedFiles = [...pendingAttachments, ...selectedFiles];
    if (combinedFiles.length > 10) {
      setAttachmentError('You can attach up to 10 files to one message.');
      return;
    }

    const totalSize = combinedFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > appConstants.maxEmailAttachmentTotalSize) {
      setAttachmentError('Attachments can total up to 20 MB per message.');
      return;
    }

    const invalidFile = selectedFiles.find(file =>
      file.size === 0 ||
      file.size > appConstants.maxFileSize ||
      (file.type && !appConstants.allowedFileTypes.includes(file.type))
    );
    if (invalidFile) {
      setAttachmentError(
        invalidFile.size === 0
          ? `${invalidFile.name} is empty and cannot be attached.`
          : invalidFile.size > appConstants.maxFileSize
            ? `${invalidFile.name} exceeds the 10 MB attachment limit.`
            : `${invalidFile.name} is not an allowed attachment type.`,
      );
      return;
    }

    setPendingAttachments(combinedFiles);
    setUploadedAttachmentIds([]);
    setAttachmentError(null);
    setMutationError(null);
  }, [pendingAttachments]);

  const removePendingAttachment = useCallback((index: number) => {
    setPendingAttachments(files => files.filter((_, fileIndex) => fileIndex !== index));
    setUploadedAttachmentIds([]);
    setAttachmentError(null);
  }, []);

  const prepareAttachmentIds = useCallback(async (): Promise<string[]> => {
    if (!pendingAttachments.length) return [];
    if (uploadedAttachmentIds.length === pendingAttachments.length) return uploadedAttachmentIds;

    setIsUploadingAttachments(true);
    try {
      const uploads = await attachmentService.uploadFiles(pendingAttachments, ticket.id);
      const attachmentIds = uploads.map(upload => upload.id).filter(Boolean);
      if (attachmentIds.length !== pendingAttachments.length) {
        throw new Error('One or more attachments did not return a durable attachment ID.');
      }
      setUploadedAttachmentIds(attachmentIds);
      return attachmentIds;
    } finally {
      setIsUploadingAttachments(false);
    }
  }, [pendingAttachments, ticket.id, uploadedAttachmentIds]);

  const submitConversation = useCallback(async (statusChangeAfterSend?: TicketStatus) => {
    if (!replyContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setMutationError(null);
    setAttachmentError(null);
    setSuccessMessage(null);
    setDeliveryNotice(null);
    setShowSendActionsDropdown(false);

    try {
      const attachmentIds = await prepareAttachmentIds();
      const newConversation: CreateConversationRequest = {
        ticketId: ticket.id,
        content: replyContent.trim(),
        isInternal,
        conversationType: isInternal ? ConversationType.Note : ConversationType.Reply,
        statusChangeAfterSend,
        attachmentIds,
      };

      const response = await ticketService.createConversation(newConversation);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to save the message.');
      }

      await loadConversations();
      if (statusChangeAfterSend) {
        onTicketUpdate({ status: statusChangeAfterSend });
      }
      setSuccessMessage(isInternal ? 'Internal note added.' : 'Reply saved and queued for email delivery.');
      resetComposer();
    } catch (error) {
      console.error('Failed to submit conversation:', error);
      const apiError = error as Partial<ApiError>;
      const errorMessage = apiError?.message || 'Failed to save the message. Please try again.';
      const persistedDeliveryFailure = Boolean(apiError?.deliveryRecordId || apiError?.retryEndpoint);

      if (persistedDeliveryFailure) {
        await loadConversations();
        const canRetry = Boolean(apiError.retryEndpoint);
        setDeliveryNotice({
          deliveryId: canRetry ? apiError.deliveryRecordId : undefined,
          message: canRetry
            ? `${errorMessage} The reply was saved; do not send it again. Retry the failed delivery instead.`
            : `${errorMessage} The reply was saved; do not send it again. Wait for a provider status update before taking further action.`,
        });
        resetComposer();
      } else {
        setMutationError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [isInternal, isSubmitting, loadConversations, onTicketUpdate, prepareAttachmentIds, replyContent, resetComposer, ticket.id]);

  const handleReplySubmit = useCallback(() => {
    void submitConversation();
  }, [submitConversation]);

  const handleSendWithAction = useCallback((statusChangeAfterSend?: TicketStatus) => {
    void submitConversation(statusChangeAfterSend);
  }, [submitConversation]);

  const handleRetryDelivery = useCallback(async (deliveryId: string) => {
    if (retryingDeliveryIds.has(deliveryId)) return;

    setRetryingDeliveryIds(current => new Set(current).add(deliveryId));
    setRetryErrors(current => {
      const next = { ...current };
      delete next[deliveryId];
      return next;
    });
    setSuccessMessage(null);

    try {
      const response = await ticketService.retryOutboundEmail(deliveryId);
      if (!response.success) throw new Error(response.message || 'Email delivery retry failed.');
      await loadConversations();
      setDeliveryNotice(null);
      setSuccessMessage('Email delivery retry queued.');
    } catch (error) {
      const message = (error as Partial<ApiError>)?.message || 'Email delivery retry failed.';
      await loadConversations();
      setRetryErrors(current => ({ ...current, [deliveryId]: message }));
    } finally {
      setRetryingDeliveryIds(current => {
        const next = new Set(current);
        next.delete(deliveryId);
        return next;
      });
    }
  }, [loadConversations, retryingDeliveryIds]);

  const handleComposerKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      void submitConversation();
    }
  }, [submitConversation]);

  const handleAttachmentDownload = useCallback(async (attachmentId: string, fileName: string) => {
    setAttachmentDownloadErrors(current => {
      const next = { ...current };
      delete next[attachmentId];
      return next;
    });
    try {
      await ticketService.downloadAttachment(attachmentId, fileName);
    } catch (error) {
      setAttachmentDownloadErrors(current => ({
        ...current,
        [attachmentId]: (error as Partial<ApiError>)?.message || 'The attachment could not be downloaded.',
      }));
    }
  }, []);

  // Calculate dropdown position to avoid footer overlap
  const calculateDropdownPosition = useCallback(() => {
    if (!sendDropdownRef.current) return;
    
    const rect = sendDropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 280; // Approximate height of dropdown
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // If not enough space below but enough above, position upward
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  }, []);

  // Handle dropdown toggle with position calculation
  const handleDropdownToggle = useCallback(() => {
    if (!showSendActionsDropdown) {
      calculateDropdownPosition();
    }
    setShowSendActionsDropdown(!showSendActionsDropdown);
  }, [showSendActionsDropdown, calculateDropdownPosition]);

  // Close send actions dropdown when clicking outside and handle scroll repositioning
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sendDropdownRef.current && !sendDropdownRef.current.contains(event.target as Node)) {
        setShowSendActionsDropdown(false);
      }
    };

    const handleScroll = () => {
      if (showSendActionsDropdown) {
        calculateDropdownPosition();
      }
    };

    if (showSendActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Use capture phase
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [showSendActionsDropdown, calculateDropdownPosition]);

  // Filter conversations based on selected filter
  const filteredConversations = Array.isArray(conversations) ? conversations
    .filter(conv => {
      switch (filterType) {
        case 'public':
          return getConversationKind(conv) === 'reply' && !conv.isInternal;
        case 'internal':
          return getConversationKind(conv) === 'note' && canEdit;
        case 'activity':
          return getConversationKind(conv) === 'activity' && canEdit;
        default:
          return getConversationKind(conv) === 'reply' || canEdit;
      }
    })
    .sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    }) : [];

  // Format relative time using Sydney timezone
  const formatRelativeTime = (dateString: string) => {
    return getRelativeTimeSydney(dateString);
  };

  // Format full timestamp using Sydney timezone
  const formatTimestamp = (dateString: string) => {
    return formatWithTimezone(dateString, true);
  };

  // Get conversation icon
  const getConversationIcon = (conv: Conversation) => {
    const kind = getConversationKind(conv);
    if (kind === 'note') {
      return (
        <div className="h-4 w-4 rounded-full bg-yellow-400 text-white flex items-center justify-center ring-2 ring-white" title="Internal Note">
          <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} 
              d="M12 15v2m-4-2v2m8-2v2M3 21h18M3 10h18M4 6l8-2 8 2v4H4V6z" />
          </svg>
        </div>
      );
    }
    
    switch (kind) {
      case 'reply':
        return (
          <div className="h-4 w-4 rounded-full bg-blue-500 text-white flex items-center justify-center ring-2 ring-white" title="Reply">
            <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} 
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="h-4 w-4 rounded-full bg-gray-500 text-white flex items-center justify-center ring-2 ring-white" title={getConversationLabel(conv)}>
            <svg className="h-2.5 w-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} 
                d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </div>
        );
    }
  };

  // Toggle message expansion
  const toggleMessageExpansion = (messageId: string) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(messageId)) {
      newExpanded.delete(messageId);
    } else {
      newExpanded.add(messageId);
    }
    setExpandedMessages(newExpanded);
  };

  // Quick reply templates
  const replyTemplates = [
    {
      id: 'thanks',
      name: 'Thank Customer',
      content: 'Thank you for contacting us. I\'ll look into this right away and get back to you shortly.',
      category: 'Customer Service'
    },
    {
      id: 'more-info',
      name: 'Request More Info',
      content: 'To better assist you, could you please provide the following additional details:\n\n1. \n2. \n3. \n\nThis will help me understand the issue better and provide an accurate solution.',
      category: 'Information Gathering'
    },
    {
      id: 'escalated',
      name: 'Escalated to Technical Team',
      content: 'I\'ve escalated this to our technical team for investigation. They will review the details and provide a solution. I\'ll update you as soon as I hear back from them.',
      category: 'Escalation'
    },
    {
      id: 'resolved',
      name: 'Issue Resolved',
      content: 'This issue has been resolved. Please test the solution and let me know if you experience any further problems. If you need additional assistance, don\'t hesitate to reach out.',
      category: 'Resolution'
    }
  ];

  return (
    <div className={clsx('flex flex-col h-full bg-white', className)}>
      {/* Conversation Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold text-gray-900">Conversation</h2>
          <Badge variant="secondary" size="sm">{filteredConversations.length}</Badge>
        </div>
        
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-white rounded-lg border border-gray-200 p-1">
            <Button
              variant={filterType === 'all' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setFilterType('all')}
              className="px-3 py-1 text-xs"
            >
              All
            </Button>
            <Button
              variant={filterType === 'public' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setFilterType('public')}
              className="px-3 py-1 text-xs"
            >
              Public
            </Button>
            {canEdit && (
              <>
                <Button
                  variant={filterType === 'internal' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterType('internal')}
                  className="px-3 py-1 text-xs"
                >
                  Notes
                </Button>
                <Button
                  variant={filterType === 'activity' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterType('activity')}
                  className="px-3 py-1 text-xs"
                >
                  Activity
                </Button>
              </>
            )}
          </div>

          {/* Sort Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={`Sort ${sortOrder === 'asc' ? 'newest first' : 'oldest first'}`}
            className="px-2"
          >
            <svg className={clsx('w-4 h-4', sortOrder === 'desc' && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
            </svg>
          </Button>
          
          {/* Reply Button */}
          {canReply && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowReplyForm(!showReplyForm)}
              disabled={showReplyForm}
            >
              Reply
            </Button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Conversation Thread */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Conversation Messages */}
          <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-4 space-y-6">
            {successMessage && (
              <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                {successMessage}
              </div>
            )}
            {deliveryNotice && (
              <div role="alert" className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 sm:flex-row sm:items-center sm:justify-between">
                <span>{deliveryNotice.message}</span>
                {deliveryNotice.deliveryId && (
                  <Button
                    variant="danger"
                    size="sm"
                    loading={retryingDeliveryIds.has(deliveryNotice.deliveryId)}
                    onClick={() => void handleRetryDelivery(deliveryNotice.deliveryId!)}
                    className="shrink-0"
                  >
                    Retry delivery
                  </Button>
                )}
              </div>
            )}
            {isLoadingConversations ? (
              <div className="flex items-center justify-center py-8">
                <Loading size="md" text="Loading conversation..." />
              </div>
            ) : conversationError ? (
              <div role="alert" className="text-center py-8 px-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
                <p className="font-medium">Conversation history could not be loaded</p>
                <p className="text-sm mt-1">{conversationError}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => void loadConversations()}>
                  Retry
                </Button>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p>No conversation messages found.</p>
              </div>
            ) : (
              filteredConversations.map((conversation) => {
                const conversationKind = getConversationKind(conversation);
                const isExpanded = expandedMessages.has(conversation.id);
                const isLongMessage = conversation.content.length > 300;
                const latestDelivery = getLatestEmailDelivery(conversation);
                const deliveryPresentation = latestDelivery
                  ? getEmailDeliveryPresentation(latestDelivery.status)
                  : undefined;
                
                return (
                  <div
                    key={conversation.id}
                    className={clsx(
                      'flex flex-col gap-3 p-4 rounded-lg group sm:flex-row sm:gap-4',
                      conversationKind === 'activity' ? 'bg-gray-50 border border-gray-200' :
                      conversationKind === 'note' ? 'bg-yellow-50 border border-yellow-200' :
                      (conversation.user?.role ?? conversation.author?.role) === UserRole.EndUser ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-200'
                    )}
                  >
                    {/* Avatar and Icon */}
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <Avatar
                          name={conversation.user?.fullName || conversation.author?.fullName || 'Unknown User'}
                          src={conversation.user?.profileImageUrl || conversation.author?.profileImageUrl}
                          size="md"
                          className="ring-2 ring-white"
                        />
                        <div className="absolute -bottom-1 -right-1">
                          {getConversationIcon(conversation)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Message Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <span className="break-words text-sm font-medium text-gray-900">
                            {conversation.user?.fullName || conversation.author?.fullName || 'Unknown User'}
                          </span>
                          <Badge variant={conversationKind === 'note' ? 'warning' : 'secondary'} size="sm">
                            {getConversationLabel(conversation)}
                          </Badge>
                          {deliveryPresentation && (
                            <Badge
                              variant={deliveryPresentation.variant}
                              size="sm"
                              data-testid={`delivery-status-${latestDelivery?.id}`}
                            >
                              {deliveryPresentation.label}
                            </Badge>
                          )}
                          <Badge variant="secondary" size="sm">
                            {getRoleDisplayName(conversation.user?.role ?? conversation.author?.role)}
                          </Badge>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <time 
                            dateTime={conversation.createdAt}
                            title={formatTimestamp(conversation.createdAt)}
                            className="text-sm text-gray-500"
                          >
                            {formatRelativeTime(conversation.createdAt)}
                          </time>
                          
                          {/* Message Actions */}
                          <div className="flex items-center space-x-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                // Quote this message in reply
                                const quotedContent = `> ${conversation.content.split('\n').join('\n> ')}\n\n`;
                                replyEditorRef.current?.insertText(quotedContent);
                                setShowReplyForm(true);
                              }}
                              title="Quote this message"
                              className="px-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </Button>
                          </div>
                        </div>
                      </div>

                      {(conversation.isFromEmail || conversation.emailDirection !== undefined) && (
                        <dl className="mb-3 grid gap-1 text-xs text-gray-600 sm:grid-cols-2">
                          {conversation.fromEmail && (
                            <div className="min-w-0">
                              <dt className="inline font-medium text-gray-700">From: </dt>
                              <dd className="inline break-all">{conversation.fromEmail}</dd>
                            </div>
                          )}
                          {conversation.toEmails && (
                            <div className="min-w-0">
                              <dt className="inline font-medium text-gray-700">To: </dt>
                              <dd className="inline break-all">{conversation.toEmails}</dd>
                            </div>
                          )}
                        </dl>
                      )}
                      
                      {/* Message Body */}
                      <RichTextRenderer
                        content={conversation.content}
                        htmlContent={conversation.htmlContent}
                        maxLength={isExpanded ? undefined : 300}
                        showExpandButton={isLongMessage}
                        onExpand={() => toggleMessageExpansion(conversation.id)}
                        isExpanded={isExpanded}
                        className="text-sm"
                      />

                      {latestDelivery && [EmailDeliveryStatus.Failed, EmailDeliveryStatus.Bounced].includes(latestDelivery.status) && (
                        <div role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <span>
                              {latestDelivery.errorMessage ||
                                (latestDelivery.status === EmailDeliveryStatus.Bounced
                                  ? 'The customer mail server bounced this message.'
                                  : 'The email provider did not accept this delivery.')}
                            </span>
                            {latestDelivery.direction === EmailDirection.Outbound && latestDelivery.status === EmailDeliveryStatus.Failed && (
                              <Button
                                variant="danger"
                                size="sm"
                                loading={retryingDeliveryIds.has(latestDelivery.id)}
                                onClick={() => void handleRetryDelivery(latestDelivery.id)}
                                className="shrink-0"
                              >
                                Retry delivery
                              </Button>
                            )}
                          </div>
                          {retryErrors[latestDelivery.id] && (
                            <p className="mt-2 font-medium">Retry failed: {retryErrors[latestDelivery.id]}</p>
                          )}
                        </div>
                      )}
                      
                      {/* Attachments */}
                      {conversation.attachments && conversation.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {conversation.attachments.map((attachment) => {
                            const displayName = getAttachmentDisplayName(attachment);
                            const sizeBytes = getAttachmentSize(attachment);
                            return (
                            <div key={attachment.id} className="flex min-w-0 flex-wrap items-center gap-2 rounded border bg-gray-50 p-2">
                              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <button
                                type="button"
                                className="min-w-0 break-all text-left text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                onClick={() => void handleAttachmentDownload(attachment.id, displayName)}
                                aria-label={`Download ${displayName}`}
                              >
                                {displayName}
                              </button>
                              <span className="text-xs text-gray-500">{attachment.contentType || 'Unknown content type'}</span>
                              <span className="text-xs text-gray-500">
                                {attachment.formattedSize || `${(sizeBytes / 1024).toFixed(1)} KB`}
                              </span>
                              {attachmentDownloadErrors[attachment.id] && (
                                <span role="alert" className="basis-full text-xs font-medium text-red-700">
                                  {attachmentDownloadErrors[attachment.id]}
                                </span>
                              )}
                            </div>
                          );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            
            <div ref={conversationEndRef} />
          </div>

          {/* Reply Form */}
          {showReplyForm && canReply && (
            <div className="border-t border-gray-200 bg-white">
              <div className="space-y-4 p-4 sm:p-6" onKeyDown={handleComposerKeyDown}>
                {/* Rich Text Editor */}
                <div>
                  <RichTextEditor
                    ref={replyEditorRef}
                    placeholder="Type your reply here..."
                    label="Reply message"
                    description="Use the toolbar above to format your reply. Press Ctrl+Enter to send."
                    onChange={handleReplyContentChange}
                    disabled={isSubmitting || isUploadingAttachments}
                    enableMentions={false}
                    enableTemplates={true}
                    enableSpellCheck={true}
                    templates={replyTemplates}
                    autoSaveInterval={30000}
                    data-reply-editor
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="replyAttachments" className="block text-sm font-medium text-gray-700">
                    Attachments
                  </label>
                  <input
                    id="replyAttachments"
                    type="file"
                    multiple
                    accept={appConstants.allowedFileTypes.join(',')}
                    onChange={handleAttachmentSelection}
                    disabled={isSubmitting || isUploadingAttachments}
                    className="block w-full min-w-0 text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500">Up to 10 files, 10 MB each.</p>
                  {pendingAttachments.length > 0 && (
                    <ul aria-label="Selected attachments" className="space-y-2">
                      {pendingAttachments.map((file, index) => (
                        <li key={`${file.name}-${file.lastModified}-${index}`} className="flex min-w-0 flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
                          <span className="min-w-0 flex-1 break-all font-medium text-gray-800">{file.name}</span>
                          <span className="text-gray-500">{file.type || 'Unknown content type'}</span>
                          <span className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                          <button
                            type="button"
                            onClick={() => removePendingAttachment(index)}
                            disabled={isSubmitting || isUploadingAttachments}
                            className="rounded px-2 py-1 font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                            aria-label={`Remove ${file.name}`}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {attachmentError && (
                    <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {attachmentError}
                    </div>
                  )}
                </div>

                {/* Internal Note Toggle */}
                {canEdit && (
                  <div className="flex items-center">
                    <input
                      id="isInternal"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      disabled={isSubmitting || isUploadingAttachments}
                    />
                    <label htmlFor="isInternal" className="ml-2 text-sm text-gray-700">
                      Internal note (not visible to requester)
                    </label>
                  </div>
                )}

                {mutationError && (
                  <div role="alert" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                    {mutationError}
                  </div>
                )}
                
                {/* Form Actions */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Send Button with Dropdown - Only show dropdown for external replies */}
                    {!isInternal ? (
                      <div className="relative" ref={sendDropdownRef}>
                        <div className="flex">
                          <Button
                            type="button"
                            variant="primary"
                            onClick={handleReplySubmit}
                            loading={isSubmitting || isUploadingAttachments}
                            disabled={!replyContent.trim() || isSubmitting || isUploadingAttachments}
                            className="rounded-r-none border-r border-primary-600"
                          >
                            {isUploadingAttachments ? 'Uploading...' : isSubmitting ? 'Sending...' : 'Send'}
                          </Button>
                          <button
                            type="button"
                            onClick={handleDropdownToggle}
                            disabled={isSubmitting || isUploadingAttachments}
                            aria-label="More send actions"
                            aria-expanded={showSendActionsDropdown}
                            className="px-2 bg-primary-600 hover:bg-primary-700 text-white rounded-r-md border-l border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                          >
                            <svg 
                              className={clsx(
                                "w-4 h-4 transition-transform duration-200",
                                showSendActionsDropdown && dropdownPosition === 'top' && "rotate-180"
                              )} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Send Actions Dropdown */}
                        {showSendActionsDropdown && (
                          <div className={clsx(
                            "absolute left-0 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-50",
                            dropdownPosition === 'top' 
                              ? "bottom-full mb-1" 
                              : "top-full mt-1"
                          )}>
                            {sendActions.map((action, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => handleSendWithAction(action.value)}
                                disabled={isSubmitting || isUploadingAttachments}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 first:rounded-t-md last:rounded-b-md border-b border-gray-100 last:border-b-0 disabled:opacity-50"
                              >
                                <div className="flex items-start space-x-3">
                                  <span className="text-lg">{action.icon}</span>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">{action.label}</div>
                                    <div className="text-xs text-gray-500">{action.description}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleReplySubmit}
                        loading={isSubmitting || isUploadingAttachments}
                        disabled={!replyContent.trim() || isSubmitting || isUploadingAttachments}
                      >
                        {isUploadingAttachments ? 'Uploading...' : isSubmitting ? 'Sending...' : 'Add Internal Note'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetComposer}
                      disabled={isSubmitting || isUploadingAttachments}
                    >
                      Cancel
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Ctrl+Enter</kbd>
                    <span>to send</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
